'use client';
import React from 'react';

import { Tooltip } from '@mui/material';

import { FloatingBox, CopyButton } from './SearchResultItem.styled';
import { useAuthContext } from '@/ui/components/AuthProvider/context';
import Icon from '@/ui/components/Icon';
import { log } from '@/utils/logger';

const getFirstWords = (text: string) => {
  const textWithoutTags = text.replace(/<[^>]*>/g, '');
  const textWithoutSpecialChars = textWithoutTags.replace(/[^a-zA-Z0-9 ]/g, '');
  const firstWords = textWithoutSpecialChars.split(' ').slice(0, 3);
  return firstWords ? firstWords.join(' ') : '';
};

export default function EmployeeActions({ href, title, description }) {
  const { can, actions, isUserLoggedIn } = useAuthContext();

  const copyLink = React.useCallback(({ href, text }) => {
    const url = new URL(href);

    if (text) {
      const firstWords = getFirstWords(text);
      url.hash = `#text${encodeURIComponent(firstWords)}`;
    }

    navigator.clipboard.writeText(url.toString()).then(
      () => {
        log.info('Link copied to clipboard');
      },
      () => {
        log.error('Failed to copy link to clipboard');
      }
    );
  }, []);

  const copyLinkAsText = React.useCallback(async ({ href, text }) => {
    const html = `<a href="${href}">${text.replace(' | Prusa Knowledge Base', '')}</a>`;
    const blob = new Blob([html], { type: 'text/html' });
    const clipboardItem = new ClipboardItem({
      'text/html': blob,
      'text/plain': new Blob([(document.createElement('div').textContent = html)], { type: 'text/plain' }),
    });

    await navigator.clipboard.write([clipboardItem]);

    try {
      await navigator.clipboard.write([clipboardItem]);
    } catch (err) {
      console.error('Failed to copy rich text link:', err);
    }
  }, []);

  return (
    <>
      {isUserLoggedIn && can(actions.copy_links) && (
        <FloatingBox className="showOnHover">
          <Tooltip title={'Copy link'} placement="top" arrow>
            <CopyButton onClick={() => copyLink({ href, text: '' })}>
              <Icon name="link" size="xl" />
            </CopyButton>
          </Tooltip>

          <Tooltip title={'Copy link with scroll to text'} placement="top" arrow>
            <CopyButton onClick={() => copyLink({ href, text: description })}>
              <Icon name="arrow-down-to-line" size="xl" />
            </CopyButton>
          </Tooltip>

          <Tooltip title={'Copy heading as a link (text + link)'} placement="top" arrow>
            <CopyButton onClick={() => copyLinkAsText({ href, text: title })}>
              <Icon name="text" size="xl" />
            </CopyButton>
          </Tooltip>
        </FloatingBox>
      )}
    </>
  );
}
